import { Approval as ApprovalIcon } from '@material-ui/icons';
import React, { useEffect, useRef } from 'react';
import Card from '../../../common/card/Card';
import CardContent from '../../../common/card/CardContent';
import CardTitle from '../../../common/card/CardTitle';
import InlineDatePicker from '../../../common/inline/InlineDatePicker';
import InlineForm from '../../../common/inline/InlineForm';
import InlineFormik from '../../../common/inline/InlineFormik';
import InlineHtml from '../../../common/inline/InlineHtml';
import { useUpdateTask } from '../../tasks/Task.hooks';
import { useTaskEntityFields } from '../../workflow/Workflow.hooks';
import { useUpdateChangeOrder } from '../ChangeOrder.hook';
import {
  BaseChangeOrderCardProps,
  ChangeOrder
} from '../ChangeOrder.interface';

const ChangeOrderImplementationActionsPlanningCardFields = [
  {
    name: 'productDesignActions',
    label: 'Product (design/ software) actions',
    description:
      'Design Requirements & Specifications, Verification & validation activities'
  },
  {
    name: 'productRiskActions',
    label: 'Product Risk actions',
    description: 'Risk management (SRA, Software, Cybersecurity)'
  },
  { name: 'regulatoryActions', label: 'Regulatory actions' },
  { name: 'qualityActions', label: 'Quality Actions' },
  { name: 'labellingActions', label: 'Labelling Actions' },
  { name: 'manufacturingActions', label: 'Manufacturing actions' },
  { name: 'supplierActions', label: 'Supplier actions' },
  { name: 'itSystemsActions', label: 'IT systems actions' }
];

export interface ChangeOrderImplementationActionsPlanningCardProps
  extends BaseChangeOrderCardProps {
  changeOrder: ChangeOrder;
  setSuccessMessage: (message: string) => void;
  setErrorMessage: (message: string) => void;
}

export const ChangeOrderImplementationActionsPlanningCard: React.FC<
  ChangeOrderImplementationActionsPlanningCardProps
> = props => {
  const { changeOrder, onChange, setSuccessMessage, setErrorMessage } = props;
  const onChangeRef = useRef(onChange);
  const {
    updatedTask,
    updateError: updateTaskError,
    updateTask
  } = useUpdateTask(changeOrder.task.id || 0);
  const { updatedChangeOrder, isLoading, error, updateChangeOrder } =
    useUpdateChangeOrder(changeOrder.id);
  const { isFieldDisabled } = useTaskEntityFields(changeOrder.task);

  const initialValues: Partial<ChangeOrder> = {
    productDesignActions: changeOrder.productDesignActions || '',
    productRiskActions: changeOrder.productRiskActions || '',
    regulatoryActions: changeOrder.regulatoryActions || '',
    qualityActions: changeOrder.qualityActions || '',
    labellingActions: changeOrder.labellingActions || '',
    manufacturingActions: changeOrder.manufacturingActions || '',
    supplierActions: changeOrder.supplierActions || '',
    itSystemsActions: changeOrder.itSystemsActions || '',
    implementationNotes: changeOrder.implementationNotes || '',
    implementationDueDate: changeOrder.implementationDueDate,
    implementationCompletedDate: changeOrder.implementationCompletedDate
  };
  useEffect(() => {
    if (updatedChangeOrder?.id) {
      setSuccessMessage('Change Order successfully updated');
      onChangeRef.current?.(updatedChangeOrder);
    }
  }, [setSuccessMessage, updatedChangeOrder]);

  useEffect(() => {
    if (updatedTask) {
      changeOrder.task = Object.assign(changeOrder.task || {}, updatedTask);
      onChange?.(changeOrder);
    }
  }, [updatedTask]);

  useEffect(() => {
    if (Boolean(error || updateTaskError))
      setErrorMessage('There was an error saving');
  }, [setErrorMessage, error, updateTaskError]);

  return (
    <InlineFormik
      initialValues={initialValues}
      strict={false}
      isSubmitting={isLoading}
      error={error}
      onSubmit={(updatedValue: Partial<ChangeOrder>) => {
        const dto = { ...updatedValue };
        updateChangeOrder(dto);
      }}
    >
      <Card>
        <CardTitle>
          <ApprovalIcon />
          Implementation Actions Planning
        </CardTitle>
        <CardContent padding='large'>
          <InlineForm>
            {ChangeOrderImplementationActionsPlanningCardFields.map(
              (field, index) => (
                <InlineHtml
                  key={index}
                  name={field.name}
                  label={field.label}
                  disabled={isFieldDisabled(field.name)}
                  helperText={field.description}
                  attachments={changeOrder.task.attachments || []}
                  onChangeAttachments={attachments => {
                    updateTask({ attachments });
                    if (changeOrder.task) {
                      changeOrder.task.attachments = attachments;
                    }
                    onChange?.(changeOrder);
                  }}
                />
              )
            )}
            <InlineHtml
              name='implementationNotes'
              label='Other Implementation Notes'
              disabled={isFieldDisabled('implementationNotes')}
              helperText='Provide any additional notes here'
              attachments={changeOrder.task.attachments || []}
              onChangeAttachments={attachments => {
                updateTask({ attachments });
                if (changeOrder.task) {
                  changeOrder.task.attachments = attachments;
                }
                onChange?.(changeOrder);
              }}
            />
            <InlineDatePicker
              grid={{ lg: 6, md: 6 }}
              clearable
              name='implementationDueDate'
              label='Due Date'
              placeholder='Enter Due Date'
              disabled={isFieldDisabled('implementationDueDate')}
              autoCloseOnSelect
            />
            <InlineDatePicker
              grid={{ lg: 6, md: 6 }}
              clearable
              name='implementationCompletedDate'
              label='Completed Date'
              placeholder='Enter Completed Date'
              disabled={isFieldDisabled('implementationCompletedDate')}
              autoCloseOnSelect
            />
          </InlineForm>
        </CardContent>
      </Card>
    </InlineFormik>
  );
};
